<template>
  <v-dialog
    :value="feedbackId != null"
    max-width="600px"
    persistent
    @keydown.esc="done(false)"
  >
    <v-card :loading="$apollo.loading">
      <v-card-title>
        <span>{{ $t("management.mentorship.feedback.edit.title") }} </span>
        <v-spacer />
        <v-btn icon @click="done(false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="mentorshipFeedback">
        <v-form ref="feedbackEdit">
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="mentorshipFeedback.code"
                :label="$t('management.mentorship.feedback.column.code')"
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="2">
              <v-text-field
                v-model="mentorshipFeedback.stage"
                :label="$t('management.mentorship.feedback.column.stage')"
                hide-details="auto"
                min="1"
                type="number"
                @change="handleRevisionOrStageChange"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="2">
              <v-text-field
                v-model="mentorshipFeedback.revision"
                :label="$t('management.mentorship.feedback.column.revision')"
                hide-details="auto"
                @change="handleRevisionOrStageChange"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                :value="
                  $options.filters.dateTime(mentorshipFeedback.revisionCreated)
                "
                :label="
                  $t('management.mentorship.feedback.column.revisionCreated')
                "
                hide-details="auto"
                disabled
              />
            </v-col>
            <v-col cols="6">
              <v-checkbox
                v-model="mentorshipFeedback.mentorSubmitFirst"
                :label="
                  $t(
                    'management.mentorship.feedback.edit.submitOrder.mentorFirst'
                  )
                "
              />
            </v-col>
            <v-col cols="6">
              <v-checkbox
                v-model="mentorshipFeedback.menteeSubmitFirst"
                :label="
                  $t(
                    'management.mentorship.feedback.edit.submitOrder.menteeFirst'
                  )
                "
              />
            </v-col>
          </v-row>

          <template v-if="mentorshipFeedback.revision">
            <template v-if="!mentorshipFeedback.id">
              <v-btn
                v-for="(locale, index) in locales.filter(
                  (l) => !usedLocales.includes(l.value)
                )"
                :key="index"
                class="primary mr-3 mb-3"
                @click="addLocale(locale.value)"
                >+{{ locale.value }}</v-btn
              >
            </template>
            <template v-if="mentorshipFeedback.form.nameLocales.length">
              <v-card class="mb-3">
                <v-card-text>
                  <div>
                    {{ $t("management.mentorship.feedback.column.name") }}
                  </div>
                  <v-row class="mb-2" no-gutters>
                    <v-col>
                      <v-text-field
                        v-for="(locale, index) in usedLocales"
                        :key="`nameTextfield${index}`"
                        v-model="
                          mentorshipFeedback.form.nameLocales[index].value
                        "
                        hide-details="auto"
                        :rules="[rules.required]"
                      >
                        >
                        <template v-slot:prepend>
                          {{ locale.toUpperCase() }}
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- mentor -->

              <mentorship-feedback-form-edit
                v-if="isMentorFormInEditMode"
                :name="$t('profile.mentorship.feedback.mentorForm')"
                :formContents="mentorshipFeedback.form.mentorContents"
                :locales="usedLocales"
                @save="mentorFormSave"
                @hide="isMentorFormInEditMode = false"
                @isValid="isMentorFormValid = $event"
              />
              <mentorship-feedback-form
                v-else
                :name="$t('profile.mentorship.feedback.mentorForm')"
                @edit="
                  mentorshipFeedback.id
                    ? emitChangeRevWarning()
                    : (isMentorFormInEditMode = true)
                "
                :contents="mentorshipFeedback.form.mentorContents"
                :isFormValid="isMentorFormValid"
              />

              <!-- mentee -->
              <mentorship-feedback-form-edit
                v-if="isMenteeFormInEditMode"
                :name="$t('profile.mentorship.feedback.menteeForm')"
                :formContents="mentorshipFeedback.form.menteeContents"
                :locales="usedLocales"
                @save="menteeFormSave"
                @hide="isMenteeFormInEditMode = false"
                @isValid="isMenteeFormValid = $event"
              />
              <mentorship-feedback-form
                v-else
                :name="$t('profile.mentorship.feedback.menteeForm')"
                @edit="
                  mentorshipFeedback.id
                    ? emitChangeRevWarning()
                    : (isMenteeFormInEditMode = true)
                "
                :contents="mentorshipFeedback.form.menteeContents"
                :isFormValid="isMenteeFormValid"
              />
            </template>
          </template>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn v-if="canSave" color="primary" class="ma-2" @click="save()">
          {{ $t("dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import MentorshipFeedbackQuery from "@/graphql/queries/mentorship/feedback/MentorshipFeedback.gql";
import CreateMentorshipFeedbackQuery from "@/graphql/mutation/mentorshipFeedback/CreateMentorshipFeedback.gql";

import MentorshipFeedbackFormEdit from "@/components/management/MentorshipFeedbackFormEdit.vue";
import MentorshipFeedbackForm from "@/components/management/MentorshipFeedbackForm.vue";

import { appendNewLocale, remap } from "@/utils/mentorshipFeedback.js";

export default {
  props: {
    feedbackId: [String, Number],
    default: null,
  },

  components: {
    MentorshipFeedbackFormEdit,
    MentorshipFeedbackForm,
  },

  data() {
    return {
      isMenteeFormInEditMode: false,
      isMenteeFormValid: true,
      isMentorFormInEditMode: false,
      isMentorFormValid: true,
      rules: {
        required: (value) => !!value || this.$t("validation.required"),
      },
    };
  },

  watch: {
    "mentorshipFeedback.menteeSubmitFirst"(newVal) {
      if (newVal) {
        this.mentorshipFeedback.mentorSubmitFirst = false;
      }
    },
    "mentorshipFeedback.mentorSubmitFirst"(newVal) {
      if (newVal) {
        this.mentorshipFeedback.menteeSubmitFirst = false;
      }
    },
  },

  computed: {
    ...mapGetters(["locales"]),
    usedLocales: {
      get() {
        return this.mentorshipFeedback.form.nameLocales
          .map((nl) => nl.language)
          .filter((value, index, self) => self.indexOf(value) === index);
      },
      set() {},
    },
    canSave() {
      return (
        this.mentorshipFeedback &&
        this.mentorshipFeedback.form.nameLocales.length > 0 &&
        !this.mentorshipFeedback.id
      );
    },
  },

  methods: {
    emitChangeRevWarning() {
      this.$store.commit("push_global_message", {
        color: "warning",
        text: this.$t("validation.revisionChangeNeeded"),
      });
    },
    addLocale(locale) {
      this.mentorshipFeedback.form.nameLocales.push({
        language: locale,
        value: "",
      });
      this.isMentorFormValid = false;
      this.isMenteeFormValid = false;

      appendNewLocale(
        this.mentorshipFeedback.form.mentorContents,
        this.usedLocales
      );
      appendNewLocale(
        this.mentorshipFeedback.form.menteeContents,
        this.usedLocales
      );
    },
    mentorFormSave(content) {
      this.isMentorFormInEditMode = false;
      this.mentorshipFeedback.form.mentorContents = content;
    },
    menteeFormSave(content) {
      this.isMenteeFormInEditMode = false;
      this.mentorshipFeedback.form.menteeContents = content;
    },
    handleRevisionOrStageChange() {
      this.mentorshipFeedback.id = null;
      this.mentorshipFeedback.revisionCreated = new Date();
      return (this.mentorshipFeedback = remap(this.mentorshipFeedback));
    },

    done(isUpdated = false) {
      this.$emit("done", isUpdated);
    },
    save() {
      if (!this.$refs["feedbackEdit"].validate()) {
        return;
      }
      if (!this.isMentorFormValid || !this.isMenteeFormValid) {
        return;
      }
      const msf = remap(this.mentorshipFeedback);
      const input = { ...msf, form: JSON.stringify(msf.form) };
      this.$apollo
        .mutate({
          mutation: CreateMentorshipFeedbackQuery,
          variables: {
            in: input,
          },
        })
        .then(() => {
          this.done(true);
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: `${this.$i18n.t("error.saveFailed")} ${err}`,
          });
        });
    },
  },
  apollo: {
    mentorshipFeedback: {
      query: MentorshipFeedbackQuery,
      variables() {
        return {
          id: Number(this.feedbackId) || 0,
        };
      },
      update(res) {
        if (res.mentorshipFeedback == null) {
          return remap({
            form: {
              nameLocales: [],
              mentorContents: [],
              menteeContents: [],
            },
          });
        }

        return res.mentorshipFeedback;
      },
      skip() {
        return this.feedbackId == null;
      },
    },
  },
};
</script>
